/**
 * The interfaces a part takes part in, and joining two ports with one (issue #52).
 *
 * Two sorts are listed, because a part plays two roles:
 *
 *   - the CONTEXT: an interface written inside this definition, joining ports on
 *     the usages it is made of - `sensor.tlmOut → obc.tlmIn`. These can be
 *     removed here, because this is where they are written.
 *   - one SIDE: an interface written in some part that contains this one, with
 *     an end on this part's own port. Listed with where it is written, and
 *     removed there.
 *
 * "Connect ports" offers exactly the endpoints the server says this definition
 * can see - its own ports and each usage's - named the way the connector will
 * write them. Choosing an interface definition is optional, and is what makes
 * the connection mean something to an ICD: the def says what its two ends are
 * and what flows between them. If the ports only fit its ends the other way
 * round, the server turns them round and says so in the answer.
 */

import { useEffect, useState } from 'react'
import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import LinkIcon from '@mui/icons-material/Link'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { ApiError, api } from '../../api'
import type { ConnectionEndpoint, InterfaceFeature, PartConnection, PartDetail } from '../../schema'
import { useSchema } from '../../state/AppData'
import { useToasts } from '../../state/Toasts'
import { MONOSPACE } from '../../theme'
import ConfirmDialog from '../common/ConfirmDialog'
import ElementPicker from '../common/ElementPicker'
import HelpTip from '../common/HelpTip'
import { connectRefusal, effectiveDirection, portType } from './interfaces-model'
import { interfaceLine, portFitsEnd, tail } from '../interfaces/icd-model'
import type { WritePermission } from './parts-model'

export const INTERFACES_HELP =
  'An interface joins two ports. One written inside this definition joins ports on the ' +
  'parts it is made of, and can be removed here. One written in a part that contains ' +
  'this one, with an end on this part’s own port, is listed with where it is written. ' +
  'Typing an interface by an interface definition is what says what its ends are and ' +
  'what flows between them — the content of an interface control document.'

export const NO_INTERFACES = 'No Interfaces'

/** How an end reads in the table: `sensor.tlmOut`, with the interface end it binds. */
function endText(end: PartConnection['ends'][number]): string {
  return end.name ? `${end.name} ::> ${end.reference}` : end.reference
}

export default function InterfacesSection({
  part,
  permission,
  onChanged,
}: {
  part: PartDetail
  permission: WritePermission
  onChanged: () => void
}) {
  const toasts = useToasts()
  const [connections, setConnections] = useState<PartConnection[] | null>(null)
  const [endpoints, setEndpoints] = useState<ConnectionEndpoint[]>([])
  const [failure, setFailure] = useState('')
  const [connecting, setConnecting] = useState(false)
  const [removing, setRemoving] = useState<PartConnection | null>(null)
  const [reloads, setReloads] = useState(0)

  useEffect(() => {
    let live = true
    setFailure('')
    api
      .partConnections(part.id)
      .then((found) => live && setConnections(found))
      .catch((error: ApiError) => {
        if (!live) return
        setConnections([])
        setFailure(error.message)
      })
    // A usage has no endpoints of its own, and asking is refused - so only a
    // definition asks.
    if (part.is_definition) {
      api
        .partEndpoints(part.id)
        .then((found) => live && setEndpoints(found))
        .catch(() => live && setEndpoints([]))
    } else {
      setEndpoints([])
    }
    return () => {
      live = false
    }
  }, [part.id, part.is_definition, reloads])

  const refusal = connectRefusal(permission, part.is_definition, endpoints.length)

  const changed = () => {
    setReloads((count) => count + 1)
    onChanged()
  }

  const remove = async (connection: PartConnection) => {
    try {
      await api.disconnectPorts(part.id, connection.qualified_name)
      toasts.succeeded(`${connection.identifier || connection.name} removed`)
      changed()
    } catch (error) {
      setFailure((error as ApiError).message)
    } finally {
      setRemoving(null)
    }
  }

  return (
    <>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <Typography variant="subtitle2">Interfaces</Typography>
          <HelpTip label="What an interface is" title={INTERFACES_HELP} />
        </Box>
        <Tooltip title={refusal} describeChild>
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<LinkIcon />}
              disabled={Boolean(refusal)}
              onClick={() => setConnecting(true)}
            >
              Connect ports
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {failure && <Alert severity="error">{failure}</Alert>}

      {connections && connections.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 2, borderStyle: 'dashed', textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {NO_INTERFACES}
          </Typography>
        </Paper>
      ) : (
        <Table size="small" aria-label="Interfaces this part takes part in">
          <TableHead>
            <TableRow sx={{ '& th': { whiteSpace: 'nowrap' } }}>
              <TableCell>Interface</TableCell>
              <TableCell>Joins</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {(connections ?? []).map((connection) => (
              <TableRow key={connection.qualified_name}>
                <TableCell>
                  {connection.name || connection.identifier || connection.keyword}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', fontFamily: MONOSPACE }}
                  >
                    {connection.interface
                      ? connection.interface.name || connection.interface.identifier
                      : connection.type_name || 'untyped'}
                  </Typography>
                  {!connection.declared_here && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      declared in {connection.owner ?? 'another part'}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ fontFamily: MONOSPACE, fontSize: '0.8rem' }}>
                  {connection.ends.map((end, index) => (
                    <Box key={index} sx={{ whiteSpace: 'nowrap' }}>
                      {endText(end)}
                      {end.port && (
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 1, fontFamily: MONOSPACE }}
                        >
                          : {portType(end.port)}
                        </Typography>
                      )}
                      {!end.port && !end.part && (
                        <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1 }}>
                          unresolved
                        </Typography>
                      )}
                    </Box>
                  ))}
                </TableCell>
                <TableCell padding="checkbox">
                  {connection.declared_here && (
                    <Tooltip
                      title={permission.allowed ? 'Remove this interface' : permission.reason}
                      describeChild
                    >
                      <span>
                        <IconButton
                          size="small"
                          aria-label={`Remove ${connection.identifier || connection.name}`}
                          disabled={!permission.allowed}
                          onClick={() => setRemoving(connection)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {connecting && (
        <ConnectPortsDialog
          part={part}
          endpoints={endpoints}
          onClose={() => setConnecting(false)}
          onConnected={() => {
            setConnecting(false)
            changed()
          }}
        />
      )}
      {removing && (
        <ConfirmDialog
          open
          title="Remove this interface?"
          message={
            `${removing.name || removing.identifier} is removed from ` +
            `${part.name || part.id}. The ports stay; only the connection between them goes.`
          }
          confirmLabel="Remove"
          onConfirm={() => void remove(removing)}
          onCancel={() => setRemoving(null)}
        />
      )}
    </>
  )
}

/** One endpoint as the picker shows it: the path, and what the port is. */
function endpointOption(endpoint: ConnectionEndpoint) {
  const flows = endpoint.features
    .filter((feature) => feature.direction)
    .map((feature) =>
      `${effectiveDirection(feature.direction, endpoint.port.is_conjugate)} ${feature.name}`)
    .join(', ')
  return {
    value: endpoint.path,
    detail: portType(endpoint.port) || 'untyped port',
    note: flows || undefined,
  }
}

/**
 * Join two ports with an interface written inside this definition.
 *
 * The INTERFACE DEFINITION is asked first, because it is what decides which
 * ports may be joined: when it declares two ends, each end offers only the ports
 * that fit it - the same port def and the same side - so a connection the server
 * would refuse is not offered. With no definition chosen, any two ports may be
 * joined by an untyped interface. The line it will write is shown before it is.
 */
export function ConnectPortsDialog({
  part,
  endpoints,
  onClose,
  onConnected,
}: {
  part: PartDetail
  endpoints: ConnectionEndpoint[]
  onClose: () => void
  onConnected: () => void
}) {
  const schema = useSchema()
  const toasts = useToasts()
  const [first, setFirst] = useState('')
  const [second, setSecond] = useState('')
  const [typeName, setTypeName] = useState('')
  const [qualifiedType, setQualifiedType] = useState('')
  const [ends, setEnds] = useState<InterfaceFeature[]>([])
  const [identifier, setIdentifier] = useState('')
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')

  // The chosen definition's two ends, which filter the pickers below, and its
  // qualified name, which is what the preview writes as the type.
  useEffect(() => {
    if (!typeName) {
      setEnds([])
      setQualifiedType('')
      return undefined
    }
    let live = true
    api.interfaceFeatures(typeName)
      .then((found) => live && setEnds(found.filter((feature) => feature.is_end)))
      .catch(() => live && setEnds([]))
    api.get('interfaces', typeName)
      .then((row) => live && setQualifiedType(row.qualified_name))
      .catch(() => live && setQualifiedType(typeName))
    return () => {
      live = false
    }
  }, [typeName])

  const typed = ends.length === 2
  const fitting = (index: number) =>
    endpoints.filter((endpoint) => !typed || portFitsEnd(endpoint.port, ends[index]))
  const firstOptions = fitting(0).map(endpointOption).filter((option) => option.value !== second)
  const secondOptions = fitting(1).map(endpointOption).filter((option) => option.value !== first)

  const preview = first && second
    ? interfaceLine({
        name: name.trim() || (identifier.trim() ? '' : generatedName(first, second)),
        identifier: identifier.trim(),
        typeName: qualifiedType,
        ends: [
          { path: first, endName: typed ? ends[0].name : '' },
          { path: second, endName: typed ? ends[1].name : '' },
        ],
      })
    : ''

  const endLabel = (index: number, fallback: string) =>
    typed
      ? `${ends[index].name} : ${ends[index].is_conjugate ? '~' : ''}${ends[index].type_name}`
      : fallback

  const submit = async () => {
    setBusy(true)
    setFailure('')
    try {
      const made = await api.connectPorts(part.id, {
        first,
        second,
        type_name: typeName || undefined,
        identifier: identifier || undefined,
        name: name || undefined,
      })
      const turned = made.ends[0] !== first
      toasts.succeeded(
        `${made.identifier || made.name} joins ${made.ends.join(' to ')}` +
          (turned ? ' (turned round to fit the interface ends)' : ''),
      )
      onConnected()
    } catch (error) {
      // The refusals - a port that fits neither end, a pair already joined -
      // are sentences with no field attached, so they are shown whole.
      setFailure((error as ApiError).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Connect ports inside {part.name || part.id}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {failure && <Alert severity="error">{failure}</Alert>}
          <ElementPicker
            schema={schema}
            kind="interface"
            where={(row) => row.declared_as === 'interface'}
            valueFor={(row) => String(row.id)}
            value={typeName || null}
            onChange={(value) => {
              setTypeName(value)
              setFirst('')
              setSecond('')
            }}
            label="Interface definition"
            helperText="Choose it first: its two ends decide which ports can be joined. Optional."
          />
          {typed && firstOptions.length + secondOptions.length === 0 && (
            <Alert severity="warning">
              No port inside {part.name || part.id} fits either end of this interface. Add ports
              typed by {tail(ends[0].type_name)} first.
            </Alert>
          )}
          <ElementPicker
            options={firstOptions}
            value={first || null}
            onChange={setFirst}
            label={endLabel(0, 'From')}
            ariaLabel="From"
            required
            monospace
          />
          <ElementPicker
            options={secondOptions}
            value={second || null}
            onChange={setSecond}
            label={endLabel(1, 'To')}
            ariaLabel="To"
            required
            monospace
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              helperText="Optional; generated from the ends"
              size="small"
              sx={{ flexGrow: 1 }}
              slotProps={{ input: { sx: { fontFamily: MONOSPACE } } }}
            />
            <TextField
              label="ID"
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
              helperText="Optional, e.g. IF_TLM_1"
              size="small"
              slotProps={{ input: { sx: { fontFamily: MONOSPACE } } }}
            />
          </Stack>
          {preview && (
            <Box>
              <Typography variant="caption" color="text.secondary">Will be written as</Typography>
              <Paper
                variant="outlined"
                sx={{ p: 1, fontFamily: MONOSPACE, fontSize: '0.8rem', overflowX: 'auto' }}
              >
                {preview}
              </Paper>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void submit()}
          disabled={!first || !second || busy}
        >
          {busy ? 'Connecting…' : 'Connect'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/** The name the server generates for an interface given none. */
function generatedName(first: string, second: string): string {
  const clean = (path: string) =>
    path.replace(/'/g, '').replace(/[^A-Za-z0-9_]+/g, '_').replace(/^_+|_+$/g, '')
  return `${clean(first)}_to_${clean(second)}`
}
